import type { PostHog } from 'posthog-js'

// Outreach-mails uit het CRM linken naar servd.nl met utm_source=outreach en
// utm_campaign=<school-slug>. Alleen de landingspagina heeft die parameters; de
// pagina's daarna (bijvoorbeeld prijzen) niet. Daarom onthouden we de school als
// super property `outreach_school`: die gaat mee met elk volgend event uit deze
// browser, ook bij een later bezoek. Daarnaast krijgt deze bezoeker een person
// profile met de school, zodat hij in PostHog als die school herkenbaar is.
// Er komt geen naam of e-mailadres bij, alleen de slug uit onze eigen link.

// Zelfde vorm als de slugs in het CRM (kleine letters, cijfers, streepjes).
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const MAX_SLUG_LENGTH = 80
const OUTREACH_PARAMS = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'] as const

export function outreachSchoolFromSearch(search: string): string | null {
  const params = new URLSearchParams(search)
  if (params.get('utm_source') !== 'outreach') return null
  const slug = params.get('utm_campaign') ?? ''
  if (slug.length === 0 || slug.length > MAX_SLUG_LENGTH || !SLUG_RE.test(slug)) return null
  return slug
}

export function trackOutreachSchool(
  posthog: Pick<PostHog, 'register' | 'register_for_session' | 'unregister_for_session' | 'setPersonProperties'>,
  search: string,
): string | null {
  const school = outreachSchoolFromSearch(search)
  if (!school) return null
  // Bewaar attributie vóór de URL wordt opgeschoond, ook voor de eerste pageview.
  const params = new URLSearchParams(search)
  const campaign: Record<string, string> = {}
  for (const key of OUTREACH_PARAMS) {
    const value = params.get(key)
    if (value) campaign[key] = value
    // Een volgende mail kan medium/content/term weglaten; houd geen oud label vast.
    else posthog.unregister_for_session(key)
  }
  posthog.register_for_session(campaign)
  posthog.register({ outreach_school: school })
  posthog.setPersonProperties({ outreach_school: school }, { first_outreach_school: school })
  return school
}

// replaceState behoudt de pagina en router-state; alleen onze UTM-velden verdwijnen.
export function trackOutreachLanding(
  posthog: Pick<PostHog, 'register' | 'register_for_session' | 'unregister_for_session' | 'setPersonProperties'>,
  browser: Pick<Window, 'location' | 'history'>,
): string | null {
  const school = trackOutreachSchool(posthog, browser.location.search)
  if (!school) return null
  const url = new URL(browser.location.href)
  for (const key of OUTREACH_PARAMS) url.searchParams.delete(key)
  browser.history.replaceState(browser.history.state, '', url.pathname + url.search + url.hash)
  return school
}
